import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useBack } from '../hooks';
import api from '../api';
import { C, cs } from '../styles/common';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

function fmtDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}
function fmtTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
}

function DiffBlock({ before, after }) {
  const [open, setOpen] = useState(false);
  if (!before && !after) return null;

  // Render component list nicely
  function compList(recipe) {
    const comps = recipe?.recipe_components || [];
    if (!comps.length) return <span style={{ color: C.muted }}>None</span>;
    return comps.map((c, i) => (
      <div key={i} style={{ fontSize: '11px', color: '#9ca3af' }}>
        <span style={{ color: C.teal, fontWeight: '600', textTransform: 'capitalize' }}>{c.type}</span>
        {' '}{c.material?.name || '?'}
        <span style={{ color: '#f59e0b', marginLeft: '5px' }}>{c.percentage}%</span>
        {c.notes && <span style={{ color: C.muted, marginLeft: '5px' }}>· {c.notes}</span>}
      </div>
    ));
  }

  return (
    <div style={{ marginTop: '10px' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', color: C.muted, cursor: 'pointer',
          fontSize: '11px', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
        <i className={`fa-solid fa-chevron-${open ? 'up' : 'down'}`} style={{ fontSize: '9px' }} />
        {open ? 'Hide' : 'Show'} before / after
      </button>
      {open && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '8px' }}>
          {/* Before */}
          <div style={{ background: '#1a0a0a', border: '1px solid #ef444433', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#f87171', marginBottom: '6px',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>Before</div>
            {before?.cycle_s != null && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Cycle: {before.cycle_s}s</div>}
            {before?.notes && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Notes: {before.notes}</div>}
            {compList(before)}
          </div>
          {/* After */}
          <div style={{ background: '#0d1a0d', border: '1px solid #16a34a33', borderRadius: '8px', padding: '10px 12px' }}>
            <div style={{ fontSize: '10px', fontWeight: '700', color: '#4ade80', marginBottom: '6px',
              textTransform: 'uppercase', letterSpacing: '0.05em' }}>After</div>
            {after?.cycle_s != null && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Cycle: {after.cycle_s}s</div>}
            {after?.notes && <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '4px' }}>Notes: {after.notes}</div>}
            {compList(after)}
          </div>
        </div>
      )}
    </div>
  );
}

export default function RecipeChanges() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const [searchParams] = useSearchParams();
  const now = new Date();

  const [changes, setChanges]   = useState([]);
  const [recipes, setRecipes]   = useState([]);
  const [loading, setLoading]   = useState(true);

  const [recipeId,  setRecipeId]  = useState(searchParams.get('recipe_id') || '');
  const [month,     setMonth]     = useState(now.getMonth() + 1);
  const [year,      setYear]      = useState(now.getFullYear());
  const [allTime,   setAllTime]   = useState(!!searchParams.get('recipe_id'));

  useEffect(() => {
    api.get('/api/v1/recipes').then(r => setRecipes(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (recipeId) params.set('recipe_id', recipeId);
    if (!allTime) { params.set('month', month); params.set('year', year); }
    api.get(`/api/v1/recipes/changes?${params}`)
      .then(r => setChanges(r.data || []))
      .catch(() => setChanges([]))
      .finally(() => setLoading(false));
  }, [recipeId, month, year, allTime]);

  useEffect(() => { load(); }, [load]);

  const years = [];
  for (let y = now.getFullYear(); y >= 2023; y--) years.push(y);

  const sel = {
    background: C.surface, border: `1px solid ${C.border}`, borderRadius: '6px',
    color: C.text, padding: '5px 10px', fontSize: '12px', cursor: 'pointer',
    fontFamily: 'system-ui, sans-serif', outline: 'none',
  };

  return (
    <div style={cs.page}>
      {/* Breadcrumb */}
      <div style={cs.breadcrumb}>
        <button onClick={goBack} style={cs.backBtn}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={cs.sep}>·</span>
        <button onClick={() => navigate('/admin/history?view=retired')} style={cs.backBtn}>History</button>
        <span style={cs.sep}>·</span>
        <span style={cs.crumb}>Recipe Changes</span>
      </div>

      <div style={cs.pageHeader}>
        <div>
          <h1 style={cs.pageTitle}>Recipe Changes</h1>
          <p style={cs.pageSub}>Audit trail of all recipe modifications</p>
        </div>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '24px',
        background: C.surface, border: `1px solid ${C.border}`, borderRadius: '10px', padding: '12px 16px' }}>

        <select value={recipeId} onChange={e => setRecipeId(e.target.value)} style={sel}>
          <option value="">All recipes</option>
          {recipes.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>

        <div style={{ width: '1px', height: '20px', background: C.border }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', opacity: allTime ? 0.4 : 1, pointerEvents: allTime ? 'none' : 'auto' }}>
          <i className="fa-solid fa-calendar" style={{ fontSize: '11px', color: C.muted }} />
          <select value={month} onChange={e => setMonth(Number(e.target.value))} style={sel}>
            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))} style={sel}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>

        <button onClick={() => setAllTime(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '6px',
            background: allTime ? '#1e3a5f' : 'none',
            color: allTime ? '#93c5fd' : C.muted,
            border: `1px solid ${allTime ? '#3b82f6' : C.border}`,
            borderRadius: '6px', padding: '5px 10px', fontSize: '11px',
            cursor: 'pointer', fontWeight: '600', fontFamily: 'system-ui, sans-serif' }}>
          <i className="fa-solid fa-infinity" style={{ fontSize: '10px' }} />
          All time
        </button>

        <div style={{ marginLeft: 'auto', fontSize: '12px', color: C.muted }}>
          {loading ? 'Loading…' : `${changes.length} change${changes.length !== 1 ? 's' : ''}`}
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div style={cs.center}>Loading…</div>
      ) : changes.length === 0 ? (
        <div style={{ ...cs.center, flexDirection: 'column', gap: '8px' }}>
          <i className="fa-solid fa-flask" style={{ fontSize: '24px', color: C.muted }} />
          <span style={{ color: C.muted, fontSize: '13px' }}>No recipe changes recorded for this period</span>
          {!allTime && (
            <span style={{ color: C.muted, fontSize: '11px' }}>
              Changes are logged from this point forward. Try "All time" to see everything.
            </span>
          )}
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          {/* Timeline spine */}
          <div style={{ position: 'absolute', left: '15px', top: 0, bottom: 0, width: '2px',
            background: `linear-gradient(to bottom, ${C.teal}44, transparent)`, zIndex: 0 }} />

          {changes.map((ch, i) => (
            <div key={ch.id} style={{ display: 'flex', gap: '20px', marginBottom: '20px', position: 'relative' }}>
              {/* Dot */}
              {(() => {
                const dotColor = ch.change_type === 'created' ? '#4ade80' : ch.change_type === 'deleted' ? '#f87171' : C.teal;
                const dotIcon  = ch.change_type === 'created' ? 'fa-plus' : ch.change_type === 'deleted' ? 'fa-trash' : 'fa-pen';
                return (
                  <div style={{ flexShrink: 0, width: '32px', height: '32px', borderRadius: '50%',
                    background: '#161b22', border: `2px solid ${dotColor}`, zIndex: 1,
                    display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={`fa-solid ${dotIcon}`} style={{ fontSize: '11px', color: dotColor }} />
                  </div>
                );
              })()}

              {/* Card */}
              <div style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`,
                borderRadius: '10px', padding: '14px 18px', marginTop: '2px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: C.text }}>{ch.recipe_name || `Recipe #${ch.recipe_id}`}</span>
                      {(() => {
                        const bg  = ch.change_type === 'created' ? '#14532d' : ch.change_type === 'deleted' ? '#450a0a' : '#0f2a4a';
                        const col = ch.change_type === 'created' ? '#4ade80' : ch.change_type === 'deleted' ? '#f87171' : '#93c5fd';
                        const bdr = ch.change_type === 'created' ? '#16a34a44' : ch.change_type === 'deleted' ? '#ef444444' : '#3b82f644';
                        return (
                          <span style={{ fontSize: '10px', background: bg, color: col,
                            border: `1px solid ${bdr}`, borderRadius: '4px', padding: '1px 7px',
                            fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                            {ch.change_type}
                          </span>
                        );
                      })()}
                    </div>
                    <div style={{ fontSize: '11px', color: C.muted, marginTop: '3px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <span>
                        <i className="fa-solid fa-calendar" style={{ marginRight: '4px', fontSize: '10px' }} />
                        {fmtDate(ch.changed_at)} at {fmtTime(ch.changed_at)}
                      </span>
                      {ch.changed_by && (
                        <span>
                          <i className="fa-solid fa-user" style={{ marginRight: '4px', fontSize: '10px' }} />
                          {ch.changed_by}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Summary */}
                {ch.summary && (
                  <div style={{ marginTop: '10px', fontSize: '12px', color: '#d1d5db',
                    background: '#0f1117', borderRadius: '6px', padding: '8px 12px',
                    borderLeft: `3px solid ${C.teal}` }}>
                    {ch.summary}
                  </div>
                )}

                <DiffBlock before={ch.before_json} after={ch.after_json} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
